/**
 * Ce qui attend la validation du curé, dit avec les bons mots (tournage de
 * l'Académie du 21/10/2026, défaut D1).
 *
 * Une correction (contre-écriture d'une offrande déjà validée) attend la
 * validation du curé comme une offrande, et les services la comptent avec les
 * offrandes en attente. L'écran disait donc « 1 offrande à valider » pour une
 * correction, ou « 2 offrandes, 1 à valider » pour une offrande et sa
 * correction. Les écrans séparent maintenant les deux : « 1 correction à
 * valider », « 2 offrandes et 1 correction à valider ».
 *
 * Calcul pur : chaque écran passe le total en attente (offrandes et
 * corrections) et la part des corrections que son service lui donne.
 */

export interface EnAttenteDuCure {
  offrandes: number;
  corrections: number;
}

type Traduire = (cle: string, params?: Record<string, string | number>) => string;

function entier(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Sépare un total en attente (offrandes et corrections ensemble) de sa part
 * de corrections. Une part de corrections absente (service d'avant le
 * 22/10/2026) laisse tout en offrandes, comme avant.
 */
export function enAttenteDuCure(total: unknown, corrections?: unknown): EnAttenteDuCure {
  const t = entier(total);
  const c = Math.min(t, entier(corrections));
  return { offrandes: t - c, corrections: c };
}

/** Rien n'attend le curé. */
export function rienEnAttente(e: EnAttenteDuCure): boolean {
  return e.offrandes + e.corrections === 0;
}

/** « 1 offrande », « 1 correction », « 2 offrandes et 1 correction » ; vide si rien. */
export function detailEnAttente(e: EnAttenteDuCure, t: Traduire): string {
  return [
    e.offrandes > 0 ? t("finance.enAttenteDuCure.offrandes", { count: e.offrandes }) : "",
    e.corrections > 0 ? t("finance.enAttenteDuCure.corrections", { count: e.corrections }) : "",
  ]
    .filter(Boolean)
    .join(t("finance.enAttenteDuCure.et"));
}

/** « 1 correction à valider », « 2 offrandes et 1 correction à valider » ; vide si rien. */
export function libelleAValider(e: EnAttenteDuCure, t: Traduire): string {
  const detail = detailEnAttente(e, t);
  return detail ? t("finance.enAttenteDuCure.aValider", { detail }) : "";
}

/**
 * Compte d'une ligne de motif de la clôture : « 2 offrandes, 1 à valider »
 * sans correction ; avec une correction, « 1 offrande et 1 correction,
 * 1 correction à valider ». `count` et `enAttente` comptent les corrections ;
 * `corrections` et `correctionsEnAttente` en donnent la part.
 */
export function compteLigneOffrandes(
  q: { count?: unknown; enAttente?: unknown; corrections?: unknown; correctionsEnAttente?: unknown },
  t: Traduire,
): string {
  const tout = enAttenteDuCure(q.count, q.corrections);
  const attente = enAttenteDuCure(q.enAttente, q.correctionsEnAttente);
  if (tout.corrections === 0) {
    return rienEnAttente(attente)
      ? t("finance.cloture.page.week.recap.quetesCount", { count: tout.offrandes })
      : t("finance.cloture.page.week.recap.quetesPending", { count: tout.offrandes, pending: attente.offrandes });
  }
  const lignes = detailEnAttente(tout, t);
  if (rienEnAttente(attente)) return lignes;
  if (attente.offrandes === tout.offrandes && attente.corrections === tout.corrections) {
    return libelleAValider(attente, t);
  }
  return `${lignes}, ${libelleAValider(attente, t)}`;
}
